/**
 * Client-side error reporter for the web app. Sends 5xx failures + uncaught
 * JS errors + unhandled promise rejections to POST /api/audit/client-error,
 * which lands them in the admin audit page via the Serilog → SystemLogs sink.
 *
 * Fire-and-forget by design: never throws, never awaits from the caller's
 * perspective, never surfaces a toast on failure. If the report POST itself
 * fails (offline, backend down) the payload is queued in sessionStorage and
 * flushed on the next foreground / next successful send.
 *
 * SOLID note: this module owns all the dedup / queueing / send logic so the
 * axios interceptor and the window handlers share ONE implementation. Adding
 * another entry-point (e.g. Vue's app.config.errorHandler) is a one-liner.
 */
import axios from 'axios'

// ── Types ──────────────────────────────────────────────────────────────────
export interface ClientErrorPayload {
  message: string
  stack?: string
  context?: string
  endpoint?: string
  statusCode?: number
  platform: 'Web'
  appVersion?: string
  deviceId?: string
}

// ── Constants ──────────────────────────────────────────────────────────────
const REPORT_PATH   = '/audit/client-error'          // relative to API base
const QUEUE_KEY     = 'zav_client_error_queue'
const DEVICE_KEY    = 'zav_client_device_id'
const QUEUE_MAX     = 50
const DEDUP_WINDOW  = 5_000                          // ms — belt-and-braces on top of BE dedup
const APP_VERSION   = (import.meta as any).env?.VITE_APP_VERSION as string | undefined

// ── Device id (per-browser-tab session) ─────────────────────────────────────
// sessionStorage over localStorage: a fresh tab counts as a fresh device for
// triage, which better matches "one user, one crash context".
function ensureDeviceId(): string {
  try {
    const existing = sessionStorage.getItem(DEVICE_KEY)
    if (existing) return existing
    // crypto.randomUUID is available in every browser we support (>= Chrome 92).
    const fresh = (globalThis.crypto?.randomUUID?.() ?? `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`)
    sessionStorage.setItem(DEVICE_KEY, fresh)
    return fresh
  } catch {
    return `web-${Date.now()}`
  }
}

// ── In-memory dedup — cheap short-window guard so a render loop doesn't
//    hammer the network before the BE dedup kicks in.
const recentHashes = new Map<string, number>()
function shouldSend(p: ClientErrorPayload): boolean {
  const key = `${p.message}|${(p.stack ?? '').slice(0, 200)}|${p.platform}|${p.appVersion ?? ''}`
  const now = Date.now()
  const seen = recentHashes.get(key)
  if (seen && now - seen < DEDUP_WINDOW) return false
  recentHashes.set(key, now)
  // Bound the map — drop oldest.
  if (recentHashes.size > 200) {
    const firstKey = recentHashes.keys().next().value
    if (firstKey) recentHashes.delete(firstKey)
  }
  return true
}

// ── Queue helpers ──────────────────────────────────────────────────────────
function readQueue(): ClientErrorPayload[] {
  try {
    const raw = sessionStorage.getItem(QUEUE_KEY)
    return raw ? (JSON.parse(raw) as ClientErrorPayload[]) : []
  } catch { return [] }
}
function writeQueue(q: ClientErrorPayload[]): void {
  try { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-QUEUE_MAX))) } catch { /* quota / private mode */ }
}
function enqueue(p: ClientErrorPayload): void {
  const q = readQueue(); q.push(p); writeQueue(q)
}

// ── Resolved API base URL ──────────────────────────────────────────────────
// Deliberately resolved lazily so the reporter doesn't take a hard dep on
// api.ts (which imports the auth store and would drag half the app into any
// early-loaded module using this reporter). Instead we read VITE_API_URL and
// the current window host in the same way api.ts does — kept minimal.
function apiBase(): string {
  const configured = ((import.meta as any).env?.VITE_API_URL as string) || '/api'
  return configured
}

// ── Core send ──────────────────────────────────────────────────────────────
// Uses a bare axios instance (not the app's apiClient) so we don't recurse
// through the app's own response interceptor. Never throws. Silent on 429
// (rate-limit) — that's the BE telling us to stop, exactly what we want.
async function send(p: ClientErrorPayload): Promise<boolean> {
  try {
    await axios.post(`${apiBase()}${REPORT_PATH}`, p, {
      timeout: 8000,
      // Never include cookies/CSRF here — endpoint is AllowAnonymous and we
      // don't want an expired session to flip this into a 401 dance.
      withCredentials: false,
      headers: { 'Content-Type': 'application/json' },
    })
    return true
  } catch {
    return false
  }
}

async function flushQueue(): Promise<void> {
  const q = readQueue()
  if (q.length === 0) return
  writeQueue([])                                                    // optimistic — worst case we re-enqueue on failure
  const requeue: ClientErrorPayload[] = []
  for (const p of q) {
    const ok = await send(p)
    if (!ok) requeue.push(p)
  }
  if (requeue.length) writeQueue([...readQueue(), ...requeue])
}

// ── Public API ─────────────────────────────────────────────────────────────
export function reportClientError(input: Omit<ClientErrorPayload, 'platform' | 'deviceId' | 'appVersion'> & Partial<Pick<ClientErrorPayload, 'appVersion' | 'deviceId'>>): void {
  const payload: ClientErrorPayload = {
    message: (input.message ?? 'unknown').toString().slice(0, 4000),
    stack: input.stack?.toString().slice(0, 20_000),
    context: input.context,
    endpoint: input.endpoint,
    statusCode: input.statusCode,
    platform: 'Web',
    appVersion: input.appVersion ?? APP_VERSION,
    deviceId: input.deviceId ?? ensureDeviceId(),
  }
  if (!shouldSend(payload)) return
  // Fire-and-forget. Never let the caller await this; never propagate errors.
  ;(async () => {
    const ok = await send(payload)
    if (!ok) enqueue(payload)
    else void flushQueue()                                          // opportunistic drain of any older queued items
  })().catch(() => { /* swallow */ })
}

/** Returns true if the given URL should be skipped by the axios interceptor
 *  (prevents an infinite loop reporting the reporter's own request). */
export function isReporterEndpoint(url: string | undefined): boolean {
  if (!url) return false
  return url.includes(REPORT_PATH)
}

/** Installs global handlers for uncaught errors + rejected promises, plus a
 *  visibility listener that opportunistically flushes the offline queue. */
export function installGlobalErrorReporter(): void {
  if (typeof window === 'undefined') return
  const w = window as any
  if (w.__zavErrorReporterInstalled) return
  w.__zavErrorReporterInstalled = true

  window.addEventListener('error', (event: ErrorEvent) => {
    reportClientError({
      message: event.message || 'window.onerror',
      stack: event.error?.stack,
      context: 'window.error',
    })
  })
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const r = event.reason
    reportClientError({
      message: (r?.message ?? String(r ?? 'unhandledrejection')).toString(),
      stack: r?.stack,
      context: 'unhandledrejection',
    })
  })
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushQueue()
  })
  // Best-effort initial drain (page just loaded — carry-over from prior session).
  void flushQueue()
}
